import type { IconProps } from '@carboncopyco/icons';
import { SearchOutlineIcon } from '@carboncopyco/icons';
import { getMaxZIndex, removeUndefinedValues } from '@carboncopyco/utils';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import classNames from 'classnames';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import type { RequireAtLeastOne } from 'type-fest';
import { Button } from '../../Button';
import { InputText } from '../../Form';
import { Paper } from '../../Paper';
import { TextBoxEditorTitle } from '../../Typography';
import type { DropdownCapsuleState } from '../FilterCapsule';
import { FilterCapsule } from '../FilterCapsule';
import type { DropdownFilterItemContent, FilterCommonProps } from '../types';
import {
  MultiDropdownFilterItem,
  type RenderItem,
} from './MultiDropdownFilterItem';
import { MultiDropdownFilterLabel } from './MultiDropdownFilterLabel';

export type MultiDropdownFilterGroup<T extends string> = {
  title?: string;
  items: DropdownFilterItemContent<T>[];
  explicitHeight?: number;
};

export type MultiDropdownFilterProps<T extends string> = RequireAtLeastOne<
  {
    fixedGroup?: MultiDropdownFilterGroup<T>;
    searchableGroup?: MultiDropdownFilterGroup<T>;
  },
  'fixedGroup' | 'searchableGroup'
> & {
  selectedItems: Record<string, string>;
  onClick?: (event: React.MouseEvent) => void;
  onChange: (item: T) => void;
  leftIcon?: React.FC<IconProps>;
  renderItem?: RenderItem<T>;
  applyButton?: React.ReactNode;
  sideOffset?: number;
  header?: React.ReactNode;
} & FilterCommonProps &
  (
    | {
        onClear?: () => void;
        footer: React.ReactElement;
      }
    | {
        onClear: () => void;
        footer?: never;
      }
  );

export function MultiDropdownFilter<T extends string>({
  fixedGroup,
  searchableGroup,
  selectedItems,
  onClick,
  onChange,
  leftIcon,
  placeholder,
  disabled,
  onClear,
  header,
  footer,
  rounded,
  size,
  capsuleWidth,
  contentWidth,
  renderItem,
  applyButton,
  sideOffset = 4,
}: MultiDropdownFilterProps<T>) {
  const [capsuleState, setCapsuleState] =
    useState<DropdownCapsuleState>('default');

  useEffect(() => {
    setCapsuleState(disabled ? 'disabled' : 'default');
  }, [disabled]);

  const [textFilter, setTextFilter] = useState<string>('');
  const filteredItems =
    searchableGroup?.items.filter((item) =>
      item.label.toLowerCase().includes(textFilter.toLowerCase())
    ) || [];
  const firstSelectedItem = useMemo(
    () =>
      Object.keys(removeUndefinedValues(selectedItems)).length
        ? Object.values(selectedItems)[0]
        : undefined,
    [selectedItems]
  );

  return (
    <DropdownMenu.Root
      onOpenChange={(open) => {
        setCapsuleState(open ? 'open' : 'default');
      }}
    >
      <DropdownMenu.Trigger
        disabled={disabled}
        className={capsuleWidth === 'full' ? 'w-full' : 'initial'}
      >
        <FilterCapsule
          width={capsuleWidth}
          size={size}
          rounded={rounded}
          leftIcon={leftIcon}
          capsuleState={capsuleState}
          placeholder={placeholder}
          numberOfSelected={
            Object.keys(removeUndefinedValues(selectedItems)).length
          }
          label={firstSelectedItem}
          onClick={onClick}
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={classNames({
            'radix-dropdown-content-full-width': contentWidth === 'full',
          })}
          style={{ zIndex: getMaxZIndex() + 1 }}
          align="start"
          sideOffset={sideOffset}
          loop
          onCloseAutoFocus={(event) => event.preventDefault()}
        >
          <Paper
            radius
            shadow="md"
          >
            {header && (
              <div>
                {typeof header === 'string' ? (
                  <div className="bg-white px-3 py-2 uppercase">
                    <TextBoxEditorTitle label={header} />
                  </div>
                ) : (
                  header
                )}
              </div>
            )}
            <div
              style={{
                width: contentWidth
                  ? contentWidth === 'full'
                    ? '100%'
                    : `${contentWidth}px`
                  : 'initial',
              }}
              className="flex flex-col max-h-[360px] overflow-y-hidden"
            >
              {fixedGroup && (
                <div
                  className={classNames(
                    'flex flex-col overflow-hidden',
                    fixedGroup.explicitHeight ? 'shrink-0' : 'flex-1'
                  )}
                  style={{
                    height: `${fixedGroup.explicitHeight}px`,
                  }}
                >
                  {fixedGroup.title && (
                    <MultiDropdownFilterLabel label={fixedGroup.title} />
                  )}
                  <div className="p-1 gap-1 overflow-y-auto">
                    {fixedGroup.items.map((item) => (
                      <MultiDropdownFilterItem
                        key={item.id}
                        item={item}
                        onChange={() => {
                          onChange(item.id);
                        }}
                        checked={!!selectedItems[item.id]}
                        disabled={item.disabled}
                        renderItem={item.renderItem || renderItem}
                      />
                    ))}
                  </div>
                </div>
              )}
              {searchableGroup && (
                <div
                  className={classNames(
                    'flex flex-col overflow-y-auto',
                    searchableGroup.explicitHeight ? 'shrink-0' : 'flex-1'
                  )}
                  style={{
                    height: `${searchableGroup.explicitHeight}px`,
                  }}
                >
                  {searchableGroup.title && (
                    <MultiDropdownFilterLabel label={searchableGroup.title} />
                  )}
                  <div className="flex flex-col flex-initial p-1 overflow-hidden">
                    <div className="pb-1">
                      <InputText
                        leftElement={<SearchOutlineIcon color="#9B9AA2" />}
                        value={textFilter}
                        placeholder="Search"
                        onChange={(event) => {
                          setTextFilter(event.target.value);
                        }}
                      />
                    </div>
                    <div className="flex flex-col gap-1 flex-initial overflow-y-auto overflow-x-hidden">
                      {filteredItems.map((item) => (
                        <MultiDropdownFilterItem
                          key={item.id}
                          item={item}
                          onChange={() => {
                            onChange(item.id);
                          }}
                          checked={!!selectedItems[item.id]}
                          renderItem={item.renderItem || renderItem}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {footer || (
                <div className="flex justify-between pb-2.5 px-2.5">
                  <Button
                    link
                    label="Clear"
                    onClick={() => {
                      onClear();
                    }}
                    disabled={!Object.keys(selectedItems).length}
                  />
                  {applyButton}
                </div>
              )}
            </div>
          </Paper>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
